import { useAuthActions } from '@convex-dev/auth/react'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { revalidateLogic } from '@tanstack/react-form'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { IconEyeOutline18, IconEyeSlashOutline18 } from 'nucleo-ui-outline-18'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AuthPanel } from '~/components/auth/auth-panel'
import { AuthShell } from '~/components/auth/auth-shell'
import { GoogleButton } from '~/components/auth/google-button'
import { NewAccountEffects } from '~/components/auth/new-account-effects'
import { SignOutButton } from '~/components/auth/sign-out-button'
import { SetupFlow } from '~/components/onboarding/setup/setup-flow'
import { Button } from '~/components/ui/button'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '~/components/ui/field'
import { Input } from '~/components/ui/input'
import { Spinner } from '~/components/ui/spinner'
import { makeSignUpSchema } from '~/lib/auth'
import { useAppForm } from '~/lib/form'
import { titleFor } from '~/lib/route-title'
import { endSignUp, ensureAuth, isSignUpInProgress, markSignUpInProgress } from '~/lib/router-auth'
import { useSignOutTeardown } from '~/lib/use-sign-out-teardown'
import { convexErrorCode } from '~convex/lib/errorCodes'

/**
 * How long the questions wait for Convex to confirm the token before giving
 * up and returning to the form. Generous: it is the handshake after a
 * successful credential exchange, not a network request, and cutting a slow
 * one short would throw away an account that was created perfectly well.
 */
const HANDSHAKE_GRACE_MS = 20_000

// Matches the ConvexError thrown by `createOrUpdateUser` when a password
// sign-up hits an email that already belongs to a user (e.g. a Google one).
const isEmailInUseError = (error: unknown): boolean => convexErrorCode(error) === 'EMAIL_IN_USE'

// Only follow in-app paths, and never bounce back to an auth screen.
const sanitizeRedirect = (value: unknown): string => {
  if (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/sign-')
  ) {
    return value
  }
  return '/home'
}

export const Route = createFileRoute('/sign-up')({
  beforeLoad: async ({ search }) => {
    // The account exists and this tab is partway through the onboarding
    // questions, which run in this route rather than on one of their own.
    // Bouncing here would throw that away — and it WOULD fire: `AuthBridge`
    // invalidates every route the moment the sign-up handshake lands, which
    // re-runs this guard against a now-authenticated session.
    //
    // Both halves are needed. The param is the durable one, surviving a
    // refresh; the latch covers the window before it can be set, because the
    // invalidation above happens while `signIn()` is still resolving and this
    // guard would otherwise run against the URL the form was submitted from.
    if (search.step === 'setup' || isSignUpInProgress()) {
      return
    }
    const auth = await ensureAuth()
    if (auth.isAuthenticated) {
      // An already-authed visitor who clicked the pricing page's Pro CTA
      // still means it — land them on the upgrade surface via /home.
      if (search.plan === 'pro' && search.redirect === undefined) {
        throw redirect({ search: { plan: 'pro' }, to: '/home' })
      }
      throw redirect({ to: sanitizeRedirect(search.redirect) })
    }
  },
  component: RouteComponent,
  // robots.txt already disallows /sign-up, but defensive `noindex` catches
  // crawlers that ignore robots.txt and render JS.
  head: () => ({
    meta: [{ title: titleFor(msg`Sign up`) }, { content: 'noindex, nofollow', name: 'robots' }],
  }),
  // Client-only: auth runs against the Convex client, no SSR needed.
  ssr: false,
  // `redirect` carries the destination a visitor was sent away from (e.g. an
  // invitation link). `plan` carries the plan choice from the pricing page so
  // we can surface it (or pre-select it) post-signup.
  validateSearch: (
    search: Record<string, unknown>
  ): { redirect?: string; plan?: 'free' | 'pro'; step?: 'setup' } => {
    const out: { redirect?: string; plan?: 'free' | 'pro'; step?: 'setup' } = {}
    if (typeof search.redirect === 'string') {
      out.redirect = search.redirect
    }
    if (search.plan === 'free' || search.plan === 'pro') {
      out.plan = search.plan
    }
    // Set by the form itself once the account exists, so the onboarding
    // questions have a URL of their own without leaving this route: a search
    // change keeps the same route match mounted, which is what stops the
    // product panel on the right from flashing between the two halves.
    if (search.step === 'setup') {
      out.step = 'setup'
    }
    return out
  },
})

function RouteComponent() {
  const { t, i18n } = useLingui()
  const { signIn } = useAuthActions()
  const navigate = useNavigate()
  const { plan, redirect: rawRedirect, step } = Route.useSearch()
  const auth = useConvexAuth()
  // The pricing page's Pro CTA carries `plan=pro`; an explicit `redirect`
  // (e.g. an invitation link) outranks it. `/home` consumes the param and
  // forwards to the workspace's billing settings.
  const redirectTo =
    plan === 'pro' && rawRedirect === undefined ? '/home?plan=pro' : sanitizeRedirect(rawRedirect)

  // Whether this tab has an account and is answering the onboarding
  // questions. It is a URL state, not a local one, so the guard above can see
  // it — and because the alternative to holding it here is a second route,
  // which is what put a page transition between the form and the first
  // question in the first place.
  //
  // The auth check is what keeps a hand-typed `?step=setup` from rendering a
  // set of questions with nobody to answer them for.

  // While it holds a new account through those questions, this route owns an
  // authed subtree of its own — so it owns the sign-out teardown too. Without
  // this, Sign out raised a latch nobody was watching and did nothing at all:
  // the `_authenticated` gate that normally does the work is not mounted here.
  const signingOut = useSignOutTeardown()
  const inSetup = step === 'setup' && auth.isAuthenticated && !signingOut
  // "Settled, signed out" is not enough to call someone a stray: for a tick
  // after `signIn()` resolves the client reports exactly that, before the
  // handshake flips it to authenticated. Sending them back then bounces the
  // account that just signed up straight out of its own questions. The latch
  // is what tells the two apart — it is raised only across a sign-up this
  // page started, and lowered the moment the questions are on screen.
  const strayedIntoSetup =
    step === 'setup' && !(auth.isLoading || auth.isAuthenticated || isSignUpInProgress())
  const inSetupPhase = step === 'setup' && !strayedIntoSetup

  useEffect(() => {
    if (strayedIntoSetup) {
      void navigate({ replace: true, search: {}, to: '/sign-up' })
    }
  }, [navigate, strayedIntoSetup])

  // The questions are on screen and the URL says so, so the latch that got
  // them here has nothing left to protect.
  useEffect(() => {
    if (inSetup) {
      endSignUp()
    }
  }, [inSetup])

  // The one path that would otherwise never lower it: `signIn()` resolves,
  // this route swaps to the questions, and Convex never settles as
  // authenticated. `inSetup` stays false so the effect above never runs, and
  // the stray check reads the raised latch as "still signing up" — leaving
  // the tab on a spinner with no way back, and this route's own gate
  // disarmed for the rest of its life. Lowering it hands the tab to that
  // stray check, which returns it to the form.
  useEffect(() => {
    if (step !== 'setup' || auth.isAuthenticated) {
      return
    }
    const timer = setTimeout(() => {
      endSignUp()
    }, HANDSHAKE_GRACE_MS)
    return () => clearTimeout(timer)
  }, [auth.isAuthenticated, step])

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const schema = useMemo(() => makeSignUpSchema(i18n), [i18n])

  const form = useAppForm({
    defaultValues: {
      confirmPassword: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      const firstName = value.firstName.trim()
      const lastName = value.lastName.trim()
      // Raised BEFORE the credential exchange: the token lands inside it and
      // the route gate re-runs the instant it does. See `markSignUpInProgress`.
      markSignUpInProgress()
      try {
        await signIn('password', {
          // Lower-cased so sign-in later matches regardless of capitalisation.
          email: value.email.trim().toLowerCase(),
          firstName,
          flow: 'signUp',
          lastName,
          name: `${firstName} ${lastName}`,
          password: value.password,
        })
        // An explicit `redirect` (an invitation link) means this person is
        // joining someone else's populated studio: there is nothing to set
        // up, so honour it. Everyone else stays on this route and its left
        // column becomes the first question — the account exists, and the
        // studio seeds behind them while they answer.
        if (rawRedirect === undefined) {
          await navigate({
            replace: true,
            search: plan === undefined ? { step: 'setup' } : { plan, step: 'setup' },
            to: '/sign-up',
          })
        } else {
          endSignUp()
          await navigate({ to: sanitizeRedirect(rawRedirect) })
        }
      } catch (error) {
        endSignUp()
        if (isEmailInUseError(error)) {
          toast.error(
            t`An account with this email already exists. Sign in instead. If you signed up with Google, use the Google button.`
          )
          return
        }
        console.error('Sign-up error:', error)
        toast.error(
          navigator.onLine
            ? t`Could not create the account. Please try again.`
            : t`You're offline. Check your connection and try again.`
        )
      }
    },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
  })

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true)
    try {
      await signIn('google', { redirectTo })
    } catch (error) {
      console.error('Google sign-up error:', error)
      toast.error(
        navigator.onLine
          ? t`Could not create the account with Google`
          : t`You're offline. Check your connection and try again.`
      )
      // On success the page redirects away, so only re-enable on failure.
      setGoogleLoading(false)
    }
  }

  // The account exists: the shell stays exactly where it is, and only the
  // column inside it changes. Nothing navigates, so the product panel on the
  // right is never unmounted — signing up and being asked about yourself are
  // one screen with two halves, not two pages. Both branches below return an
  // `AuthShell` at the same position, which is what makes React update it
  // rather than tear it down and build a new one.
  //
  // `NewAccountEffects` comes along because this route sits OUTSIDE the
  // authenticated shell that normally hosts them: without it the studio would
  // never seed and the sign-up would never be reported.
  if (inSetupPhase) {
    return (
      <AuthShell headerExtra={inSetup ? <SignOutButton /> : null}>
        {inSetup ? (
          <>
            <NewAccountEffects />
            <SetupFlow plan={plan === 'pro' ? 'pro' : undefined} />
          </>
        ) : (
          // Either `signIn()` has resolved but Convex has not confirmed the
          // token yet — the questions need an identity to write to — or a
          // sign-out is tearing this subtree down before the token is nulled.
          <Spinner />
        )}
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <AuthPanel
        altPrompt={
          <Trans>
            Already have an account?{' '}
            <Link
              className="font-medium text-foreground underline-offset-4 transition-colors hover:underline"
              to="/sign-in"
            >
              Sign in
            </Link>
          </Trans>
        }
        googleButton={
          <GoogleButton
            disabled={googleLoading}
            label={t`Sign up with Google`}
            onClick={handleGoogleSignUp}
          />
        }
        subtitle={<Trans>Fill in the details below to get started</Trans>}
        title={<Trans>Create your account</Trans>}
      >
        <form
          className="animate-fade-up space-y-5"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          style={{ animationDelay: '0ms' }}
        >
          <FieldGroup>
            {/* Dev-only auto-fill button */}
            {import.meta.env.DEV ? (
              <Button
                className="w-full"
                onClick={() => {
                  const timestamp = Date.now()
                  form.setFieldValue('firstName', 'Test')
                  form.setFieldValue('lastName', 'User')
                  form.setFieldValue('email', `test+${timestamp}@example.com`)
                  form.setFieldValue('password', 'Test1234')
                  form.setFieldValue('confirmPassword', 'Test1234')
                }}
                type="button"
                variant="secondary"
              >
                [DEV] Auto-fill test data
              </Button>
            ) : null}

            {/* Name fields */}
            <Field className="grid gap-3 sm:grid-cols-2">
              <form.Field name="firstName">
                {(field) => (
                  <Field>
                    <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                      <Trans>First name</Trans>
                    </FieldLabel>
                    <Input
                      aria-describedby={`${field.name}-error`}
                      aria-invalid={field.state.meta.errors.length > 0}
                      autoComplete="given-name"
                      className="h-11 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t`John`}
                      type="text"
                      value={field.state.value}
                    />
                    <FieldError
                      className="text-xs"
                      errors={field.state.meta.errors}
                      id={`${field.name}-error`}
                    />
                  </Field>
                )}
              </form.Field>

              <form.Field name="lastName">
                {(field) => (
                  <Field>
                    <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                      <Trans>Last name</Trans>
                    </FieldLabel>
                    <Input
                      aria-describedby={`${field.name}-error`}
                      aria-invalid={field.state.meta.errors.length > 0}
                      autoComplete="family-name"
                      className="h-11 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t`Smith`}
                      type="text"
                      value={field.state.value}
                    />
                    <FieldError
                      className="text-xs"
                      errors={field.state.meta.errors}
                      id={`${field.name}-error`}
                    />
                  </Field>
                )}
              </form.Field>
            </Field>

            {/* Email field */}
            <form.Field name="email">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Email</Trans>
                  </FieldLabel>
                  <Input
                    aria-describedby={`${field.name}-error`}
                    aria-invalid={field.state.meta.errors.length > 0}
                    autoComplete="email"
                    className="h-11 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                    id={field.name}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t`you@email.com`}
                    type="email"
                    value={field.state.value}
                  />
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </form.Field>

            {/* Password field */}
            <form.Field name="password">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Password</Trans>
                  </FieldLabel>
                  <div className="relative">
                    <Input
                      aria-describedby={`${field.name}-desc ${field.name}-error`}
                      aria-invalid={field.state.meta.errors.length > 0}
                      autoComplete="new-password"
                      className="h-11 pr-10 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="••••••••"
                      type={showPassword ? 'text' : 'password'}
                      value={field.state.value}
                    />
                    <Button
                      aria-label={showPassword ? t`Hide password` : t`Show password`}
                      className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                      size="icon-sm"
                      variant="ghost"
                    >
                      {showPassword ? (
                        <IconEyeSlashOutline18 className="size-5" />
                      ) : (
                        <IconEyeOutline18 className="size-5" />
                      )}
                    </Button>
                  </div>
                  <FieldDescription className="text-xs" id={`${field.name}-desc`}>
                    <Trans>At least 8 characters with one number or special character</Trans>
                  </FieldDescription>
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </form.Field>

            {/* Confirm password field */}
            <form.Field name="confirmPassword">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Confirm password</Trans>
                  </FieldLabel>
                  <div className="relative">
                    <Input
                      aria-describedby={`${field.name}-error`}
                      aria-invalid={field.state.meta.errors.length > 0}
                      autoComplete="new-password"
                      className="h-11 pr-10 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="••••••••"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={field.state.value}
                    />
                    <Button
                      aria-label={showConfirmPassword ? t`Hide password` : t`Show password`}
                      className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      size="icon-sm"
                      variant="ghost"
                    >
                      {showConfirmPassword ? (
                        <IconEyeSlashOutline18 className="size-5" />
                      ) : (
                        <IconEyeOutline18 className="size-5" />
                      )}
                    </Button>
                  </div>
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </form.Field>

            {/* Submit button */}
            <form.Subscribe>
              {(state) => (
                <Field className="pt-2">
                  <Button
                    className="h-11 w-full font-medium transition-all"
                    disabled={!state.canSubmit || state.isSubmitting}
                    type="submit"
                  >
                    {state.isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <Spinner className="size-4 text-current" />
                        <Trans>Creating account...</Trans>
                      </span>
                    ) : (
                      <Trans>Create account</Trans>
                    )}
                  </Button>
                </Field>
              )}
            </form.Subscribe>
          </FieldGroup>
        </form>
      </AuthPanel>
    </AuthShell>
  )
}
